import React, { useRef, useEffect } from "react";

// ─── ConversationThread ─────────────────────────────────────────────────────────
// Every message on this thread — email, chat, DMs, design feedback. Same store,
// same walls. This IS the work panel's signature element (spec §2).
// Auto-scrolls to the latest message on load.
const ConversationThread = ({ messages, threadId }) => {
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  // pb-6 (not py-4) gives a little extra breathing room below the
  // "No messages yet" line on mobile, where the collapsed messages area
  // sits directly above the composer border (Item 3 follow-up).
  if (!messages || messages.length === 0) {
    return (
      <div className="px-5 pt-4 pb-6">
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          No messages yet. Reply above to start the thread.
        </p>
      </div>
    );
  }

  return (
    <div className="px-5 py-4">
      <div className="space-y-3 overflow-y-auto overflow-x-hidden">
        {messages.map((msg, i) => {
          const isSystem = msg.type === "system" || msg.channel === "system";
          const isOutbound = msg.direction === "outbound" || msg.is_outbound;

          if (isSystem) {
            return (
              <div key={msg.id || i} className="text-center py-1">
                <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                  {msg.body || "System event"}
                </p>
              </div>
            );
          }

          return (
            <div
              key={msg.id || i}
              className={`rounded-lg p-3 ${
                isOutbound
                  ? "bg-[#32B1FF]/10 border border-[#32B1FF]/20 ml-6"
                  : "bg-gray-50 dark:bg-gray-800 mr-6"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {msg.from || "Unknown"}
                </span>
                {msg.channel && msg.channel !== "system" && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    via {msg.channel}
                  </span>
                )}
                {msg.at && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                    {new Date(msg.at).toLocaleDateString("en-US", {
                      month: "short", day: "numeric",
                      hour: "numeric", minute: "2-digit",
                    })}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                {msg.body || "…"}
              </p>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
    </div>
  );
};

export default ConversationThread;
